import React, { useCallback } from "react"
import { Flex, FlexProps, Box, Button } from "@chakra-ui/core"
import { useDebouncedCallback } from "use-debounce"

import SmallCaps from "../typography/SmallCaps"
import { bp, bpWithSidebar } from "../../utils/MediaQueries"
import useScrollTracker from "../../hooks/useScrollTracker"
import useLayoutMeasurements from "../../hooks/useLayoutMeasurements"
import styled from "@emotion/styled"
import { useIsSideBarOpen } from "../../hooks/useIsSidebarOpen"
import debounce from "lodash/debounce"
import IconButton from "../Buttons/IconButton"

export interface HeaderProps {
  labels: Array<string>
}

const StyledContainer = styled(Flex)`
  scrollbar-width: none;
  ::-webkit-scrollbar {
    height: 0px; /* Remove scrollbar space */
    background: transparent; /* Optional: just make scrollbar invisible */
  }
`

const ScrollButton: React.FC<{
  show: boolean
  placement: "left" | "right"
  onClick: VoidFunction
}> = ({ show, placement, onClick }) => (
  <Flex
    d={show ? "flex" : "none"}
    pos="absolute"
    top="0"
    left={placement === "left" ? "0" : undefined}
    right={placement === "right" ? "0" : undefined}
    height="100%"
    align="center"
    zIndex={1}
    background="linear-gradient(90deg, #FAFBFC 60%, transparent)"
    pr="1rem"
    transform={placement === "right" ? "rotate(180deg)" : undefined}
  >
    <IconButton
      size="1.25rem"
      icon="chevron"
      px="0.5rem"
      color="dawn"
      aria-label={`Scroll product nav to ${placement}`}
      onClick={onClick}
    />
  </Flex>
)

const Header: React.FC<FlexProps & HeaderProps> = ({
  labels,
  ...props
}: FlexProps & HeaderProps) => {
  const isSideBarOpen = useIsSideBarOpen()
  const headerRef = React.useRef<HTMLElement>(null)
  const [showLeftNav, setShowLeftNav] = React.useState(false)
  const [showRightNav, setShowRightNav] = React.useState(false)
  const currentSection = useScrollTracker(labels)
  const { contentWidthResponsive } = useLayoutMeasurements()

  const scrollToCurrentLabel = (section: string) => {
    const labelEl = document.getElementById(`${section}-label`)
    if (labelEl && headerRef?.current) {
      const labelRect = labelEl.getBoundingClientRect()
      const containerRect = headerRef.current.getBoundingClientRect()
      const left =
        labelRect.left + headerRef.current.scrollLeft - containerRect.width / 2
      headerRef.current.scrollTo({
        left,
        behavior: "smooth",
      })
    }
  }
  const debouncedScrollToCurrentLabel = useDebouncedCallback(
    scrollToCurrentLabel,
    100
  )

  React.useEffect(() => {
    debouncedScrollToCurrentLabel.callback(currentSection)
  }, [currentSection])

  const updateNavButtons = useCallback(
    debounce(() => {
      if (headerRef.current) {
        const { clientWidth, scrollWidth, scrollLeft } = headerRef.current
        if (clientWidth < scrollWidth) {
          setShowLeftNav(scrollLeft !== 0)
          setShowRightNav(scrollLeft + clientWidth < scrollWidth)
        } else {
          setShowLeftNav(false)
          setShowRightNav(false)
        }
      }
    }, 100),
    []
  )
  React.useEffect(() => {
    updateNavButtons()
    // Recalculate on window resize
    window.addEventListener("resize", updateNavButtons)
    return () => window.removeEventListener("resize", updateNavButtons)
  }, [])

  React.useEffect(() => {
    if (headerRef.current) {
      headerRef.current.addEventListener("scroll", updateNavButtons)
    }
    return () => {
      if (headerRef.current) {
        headerRef.current.removeEventListener("scroll", updateNavButtons)
      }
    }
  }, [headerRef.current])

  const handleNavClick = (label: string) => (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault()
    const el = document.getElementById(label)
    if (el) {
      window.scroll({
        top: el.offsetTop - el.offsetHeight - 80,
        left: 0,
        behavior: "smooth",
      })
    }
  }

  const handleLeftNavClick = () => {
    headerRef.current?.scrollBy({ left: -150, behavior: "smooth" })
  }
  const handleRightNavClick = () => {
    headerRef.current?.scrollBy({ left: 150, behavior: "smooth" })
  }
  return (
    <Flex
      w={contentWidthResponsive}
      h={["3.25rem", null, null, null, "4rem"]}
      bg="#FAFBFC"
      borderTop={["1px solid", null, null, null, "0px"]}
      borderBottom="1px solid"
      borderColor="dividerLine"
      // justifyContent={[null, null, null, null, "center"]}
      position="fixed"
      top={bpWithSidebar("3.5rem", "4rem", isSideBarOpen)}
      left="0px"
      zIndex={1000}
      {...props}
    >
      <StyledContainer ref={headerRef} h="100%" w="100%" overflowX="auto">
        <Box minWidth="5rem" flexGrow={1} flexShrink={0} />
        {labels.map((label) => (
          <Button
            key={label}
            id={`${label}-label`}
            h="100%"
            alignItems="center"
            borderBottom={[
              null,
              null,
              null,
              null,
              currentSection === label ? "1px solid" : "0px",
            ]}
            borderColor={[
              null,
              null,
              null,
              null,
              currentSection === label ? "sunrise" : "dawn",
            ]}
            px="0px"
            mx={bp("0.7813rem", "2.0625rem")}
            flexShrink={0}
            variantColor="none"
            borderRadius="0"
            _hover={{
              borderBottom: "1px solid",
              borderColor: currentSection === label ? "sunrise" : "dawn",
            }}
            _focus={{
              outline: "none",
              borderBottom: "1px solid",
              borderColor: currentSection === label ? "sunriseLight20" : "dawn",
            }}
            onClick={handleNavClick(label)}
          >
            <SmallCaps
              key={`bike-page-header-item-${label}`}
              color={label !== currentSection ? "dawn" : "sunrise"}
              size="sm"
              fontWeight={label !== currentSection ? "normal" : "bold"}
            >
              {label}
            </SmallCaps>
          </Button>
        ))}
        <Box minWidth="5rem" flexGrow={1} flexShrink={0} />
      </StyledContainer>
      <ScrollButton
        show={showLeftNav}
        placement="left"
        onClick={handleLeftNavClick}
      />
      <ScrollButton
        show={showRightNav}
        placement="right"
        onClick={handleRightNavClick}
      />
    </Flex>
  )
}

export default Header
